import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import useEntityStore from "../../stores/entityStore";

const ALLOWED_ENTITY_TYPES = ['doc', 'document', 'note', 'sheet', 'canvas', 'presentation', 'app_project'];

export const entityCreateCommand: SelfContainedCommand = {
  id: 'entity_create',
  description: `Create new entities.

**SUPPORTED ENTITIES:**
- Documents: doc
- Notes: note
- Sheets: sheet
- Canvas: canvas
- Presentations: presentation
- React app: app_project (opens in AppBuilderApp IDE)

**EXAMPLES:**

Create document:
{ "entityType": "doc", "name": "Research Paper" }

Create note:
{ "entityType": "note", "name": "Meeting Notes" }

Create presentation:
{ "entityType": "presentation", "name": "Q4 Review" }

Create React app (default react-spa template):
{ "entityType": "app_project", "name": "My Calculator", "icon": "Calculator" }

Create React app with specific template and icon:
{ "entityType": "app_project", "name": "My Blog", "icon": "Article", "skeleton": { "template": "nextjs-blog" } }

Create in folder:
{ "entityType": "note", "name": "Project Notes", "folderId": "group_guitar_ocean_zodiac" }

**RETURNS:** Entity ID

**app_project templates:** react-spa (default) | nextjs-shop | nextjs-shop-simple | nextjs-blog | nextjs-portfolio

**app_project icon:** REQUIRED — must be a Phosphor icon name matching the app's purpose. Examples: "Calculator", "ChartBar", "Heart", "ShoppingCart", "Notepad", "GameController", "Music", "Leaf". Creation will fail without it.
`,
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'entityType',
        type: 'string',
        description: 'Entity type: doc | note | sheet | canvas | presentation | app_project',
        required: true,
        examples: ['doc', 'note', 'sheet', 'canvas', 'presentation', 'app_project'],
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'Entity type is required';
          }
          if (!ALLOWED_ENTITY_TYPES.includes(value.toLowerCase())) {
            return `Invalid entity type "${value}". Allowed types: doc | note | sheet | canvas | presentation | app_project`;
          }
          return null;
        }
      },
      {
        name: 'name',
        type: 'string',
        description: 'Name/title for the new entity',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'Entity name cannot be empty';
          }
          if (value.length > 200) {
            return 'Entity name must be 200 characters or less';
          }
          return null;
        }
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder ID (group_*) to create entity in. Leave empty for root level.',
        required: false,
        examples: ['group_guitar_ocean_zodiac', 'group_meadow_silver_compass']
      },
      {
        name: 'skeleton',
        type: 'object',
        description: 'Custom skeleton fields for the entity (merged with auto-generated fields)',
        required: false,
      },
      {
        name: 'publish',
        type: 'boolean',
        description: 'Documents only: Set true to publish as public blog post. Default false.',
        required: false,
        examples: [true, false]
      },
      {
        name: 'initialContent',
        type: 'string',
        description: 'Documents only: Starting content in HTML format.',
        required: false
      },
      {
        name: 'icon',
        type: 'string',
        description: 'REQUIRED for app_project. Phosphor icon name matching the app\'s purpose (e.g. "Calculator", "ChartBar", "Heart", "ShoppingCart", "Notepad"). Ignored for all other entity types.',
        required: false // conditionally required for app_project — enforced in handler
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          entityType: 'sheet', 
          name: 'Budget 2026'
        },
        expectedOutput: createSuccessResult('Entity created successfully', {
          entity: {
            _id: 'sheet_abc_xyz',
            name: 'Budget 2026',
            entityType: 'Sheet'
          }
        }),
        description: 'Create a new spreadsheet',
        context: 'When user wants to create a spreadsheet for data tracking'
      },
      {
        input: { 
          entityType: 'note', 
          name: 'Meeting Notes'
        },
        expectedOutput: createSuccessResult('Entity created successfully', {
          entity: {
            _id: 'note_abc_xyz',
            name: 'Meeting Notes',
            entityType: 'Note'
          }
        }),
        description: 'Create a new note',
        context: 'When user wants to create a note for quick capture'
      },
      {
        input: { 
          entityType: 'canvas', 
          name: 'Project Board',
          folderId: 'group_projects_2024'
        },
        expectedOutput: createSuccessResult('Entity created successfully', {
          entity: {
            _id: 'canvas_abc_xyz',
            name: 'Project Board',
            entityType: 'Canvas'
          }
        }),
        description: 'Create a canvas in a folder',
        context: 'When user wants to create a visual canvas workspace'
      }
    ],
    bad: [
      {
        input: { entityType: 'invalidtype', name: 'Q1 Review' },
        error: 'Invalid entity type "invalidtype". Allowed types: doc | note | sheet | canvas | presentation | app_project',
        why: 'Only the listed entity types are supported',
        fix: 'Use a valid entityType: doc | note | sheet | canvas | presentation | app_project'
      },
      {
        input: { entityType: 'note', name: '' },
        error: 'Entity name cannot be empty',
        why: 'Empty names are not allowed',
        fix: 'Provide a meaningful name for the entity'
      }
    ]
  },
  
  performance: {
    expectedDuration: 500,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityType } = params;
    
    // Critical: Validate entityType before using it
    if (!entityType || typeof entityType !== 'string') {
      return {
        status: 'error',
        message: 'Entity type is required',
        error: { code: 'missing_entity_type', category: 'validation' as const, details: 'entityType parameter is required and must be a string' },
        format: 'plaintext'
      };
    }
    
    const normalizedType = entityType.toLowerCase();
    
    // Route to document handler for doc/document types
    if (normalizedType === 'doc' || normalizedType === 'document') {
      return handleDocumentCreate(params, context);
    }
    
    // Route to app project handler
    if (normalizedType === 'app_project') {
      return handleAppProjectCreate(params, context);
    }
    
    // Route to entity handler for standard types
    return handleEntityCreate(params, context);
  }
};

// ============================================================================
// DOCUMENT CREATION HANDLER (Complete docs_create logic)
// ============================================================================
async function handleDocumentCreate(params: any, context: CommandExecutionContext): Promise<StandardToolResult> {
  const startTime = Date.now();
  let { name = 'New Document', initialContent, folderId, publish = false } = params;
  
  // Critical: Ensure name is a valid string (handle null)
  if (name === null || name === undefined) {
    name = 'New Document';
  }
  if (typeof name !== 'string') {
    name = String(name);
  }
  
  // Get Space ID
  let spaceId = context.spaceId;
  if (!spaceId) {
    const useSpaceStore = (await import('../../stores/spaceStore')).default;
    const actualSpaceId = useSpaceStore.getState().activeSpace?.id;
    if (actualSpaceId) {
      spaceId = actualSpaceId;
    }
  }
  
  try {
    // Load user preferences for default font and page mode
    let userDefaultFont = "Calibri, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
    let userDefaultPageMode: 'page' | 'pageless' = 'page';
    
    try {
      const { userService } = await import('../../services/users/userService');
      const profile = await userService.getUserProfile();
      const docsPrefs = profile.skeleton.preferences?.docs || {};
      if (docsPrefs.defaultFont) {
        userDefaultFont = docsPrefs.defaultFont;
      }
      if (docsPrefs.defaultPageMode) {
        userDefaultPageMode = docsPrefs.defaultPageMode;
      }
    } catch {
      // Use defaults if preferences unavailable
    }
    
    // Create document entity directly using EntityService
    const { generateDocumentPrefix } = await import('../../utils/entityIdGenerator');
    const docMiniId = generateDocumentPrefix();
    const newDocEntityData: any = {
      entityType: 'Doc',
      name: name.trim(),
      icon: '📄',
      spaceId: spaceId,
      folderId: folderId || null,
      skeleton: {
        '@type': 'Doc',
        version: 'v2',
        architecture: 'unified',
        content: typeof initialContent === 'string' && initialContent.trim().length > 0 ? initialContent : '<p><span style="font-family: Calibri, sans-serif; font-size: 11pt;"></span></p>',
        docMiniId,
        temporary: false,
        groupIds: folderId ? [folderId] : [],
        in_showcase: publish === true,
        settings: {
          isContentsVisible: true,
          isChatVisible: true,
          zoomLevel: 90,
          defaultFont: userDefaultFont,
          defaultPageMode: userDefaultPageMode
        }
      },
      created_at: new Date().toISOString(),
    };
    
    // Create entity using entityStore
    const useEntityStore = (await import('../../stores/entityStore')).default;
    const newDoc = await useEntityStore.getState().actions.createEntity(newDocEntityData);
    
    // Ensure llmIdMapper registration is complete
    const docLlmId = (newDoc as any).llmId;
    const docActualId = newDoc._id || newDoc.id;
    if (docLlmId && docActualId) {
      const { llmIdMapper } = await import('../../utils/llmIdMapper');
      llmIdMapper.registerMapping(docLlmId, docActualId, 'document', 'Doc', newDoc.name);
    }
    
    // Update parent folder's childEntities for proper sidebar hierarchy
    if (folderId && docActualId) {
      const { resolveEntityId } = await import('./entityIdResolver');
      const resolution = resolveEntityId(folderId);
      const resolvedFolderId = resolution.resolved;
      
      const entityStore = useEntityStore.getState();
      const parentFolder = entityStore.entities.find(e => e._id === resolvedFolderId);
      
      if (parentFolder) {
        const currentChildren = parentFolder.skeleton?.childEntities || [];
        if (!currentChildren.includes(docActualId)) {
          const updatedChildren = [...currentChildren, docActualId];
          entityStore.actions.updateEntity(resolvedFolderId, {
            skeleton: {
              childEntities: updatedChildren
            }
          });
        }
      }
    }
    
    // Initialize Y.js in background (non-blocking)
    (async () => {
      try {
        const { docsCRDTService } = await import('../../apps/DocsApp/services/crdt/docsCRDTService');
        const { yDocumentService } = await import('../../apps/DocsApp/services/doc/internal/yDocLowLevelAPI');
        
        const { doc: yDoc } = await docsCRDTService.getYDocForDoc(docActualId);
        
        if (yDoc) {
          yDocumentService.migrateDocumentIds(yDoc, {
            docId: docActualId,
            logPrefix: '[entity_create:doc]'
          });
          
          const stats = yDocumentService.getDocumentStats(yDoc);
          
          if (!stats || stats.totalNodes === 0) {
            const yxml = yDocumentService.getXmlFragment(yDoc, 'prosemirror');
            if (yxml) {
              const { createYElement } = await import('../../apps/DocsApp/services/doc/internal/yDocLowLevelAPI');
              
              yDoc.transact(() => {
                const initialParagraph = createYElement('paragraph', {
                  content: '',
                  docId: docActualId,
                  logPrefix: '[entity_create:doc_init]'
                });
                yxml.insert(0, [initialParagraph]);
              });
            }
          }
        }
      } catch (initError) {
        console.error('[entity_create:doc] Y.js background init failed:', initError);
      }
    })();
    
    // Create window
    let windowInfo = null;
    try {
      const { useCommandRegistryStore } = await import('../../stores/commandRegistryStore');
      const { executeCommand } = useCommandRegistryStore.getState();
      
      const entityId = newDoc._id || newDoc.id;
      
      const result = (await executeCommand('space_open_window', {
        entityId
      })) as StandardToolResult;
      
      if (result.status !== 'success') {
        throw new Error(result.message || 'Failed to open window');
      }
      
      const createdWindowId = result.data?.windowId;
      
      // Mark as just created
      try {
        const useSpaceStore = (await import('../../stores/spaceStore')).default;
        const { updateWindow } = useSpaceStore.getState();
        const window = useSpaceStore.getState().windows[createdWindowId];
        if (window) {
          updateWindow(createdWindowId, {
            applicationState: {
              ...window.applicationState,
              docs: {
                ...(window.applicationState?.docs || {}),
                justCreated: true as any,
                originalName: newDoc.name || 'New Document',
                renamed: false as any,
                hasContent: false as any
              }
            }
          } as any);
        }
      } catch {
        // Non-critical: window state update failed
      }
      
      windowInfo = {
        id: createdWindowId,
        title: name.trim(),
        appType: 'docs',
        entityId,
        entityLlmId: (newDoc as any).llmId
      };
    } catch (windowError) {
      // Window failed to open — entity was still created successfully
    }
    
    const result = {
      document: {
        id: newDoc.id || newDoc._id,
        llmId: (newDoc as any).llmId,
        name: name.trim(),
        entityType: newDoc.entityType || 'Doc',
        created_at: newDoc.created_at,
        icon: newDoc.icon || '📄'
      },
      window: windowInfo
    };
    
    // Emit entity created event
    const { entityEventService } = await import('../../services/entityEventService');
    entityEventService.entityCreated(
      newDoc,
      spaceId,
      {
        windowId: windowInfo?.id,
        command: 'entity_create',
        userId: context.userId
      }
    );
    
    // Only add to context when a window was actually opened — not during bulk setup
    if (windowInfo) {
      try {
        const { useContextStore } = await import('../../components/Space/SpaceChat/store/contextStore');
        const contextStore = useContextStore.getState();
        contextStore.addDocumentReference(newDoc.llmId || newDoc.id, {
          title: name.trim(),
          appType: 'docs',
          entityType: 'Doc',
          isActive: true,
          windowId: windowInfo.id,
          documentRole: 'currentDoc'
        });
      } catch (contextError) {
        throw contextError;
      }
    }
    
    return createSuccessResult(
      `Document created successfully: "${name.trim()}"${windowInfo ? ' (with window)' : ''} Document is ready for immediate editing with doc_write.`,
      result,
      {
        format: 'json',
        metadata: {
          executionTime: Date.now() - startTime,
          method: 'self-contained',
          affectedEntities: [newDoc.id || newDoc._id, ...(windowInfo ? [windowInfo.id] : [])],
          documentName: name.trim()
        } as any,
        ui: {
          refreshContext: true,
          autoFocus: windowInfo?.id,
          resultPreview: 'summary'
        }
      }
    );
    
  } catch (error: any) {
    return createErrorResult(
      'Document creation failed',
      `Failed to create document: ${error.message || 'Unknown error'}`,
      'execution',
      { 
        data: { 
          name,
          spaceId,
          errorStack: error instanceof Error ? error.stack : undefined
        },
        metadata: { executionTime: Date.now() - startTime }
      }
    );
  }
}

// ============================================================================
// APP PROJECT CREATION HANDLER (delegates to app_builder_create)
// ============================================================================
async function handleAppProjectCreate(params: any, context: CommandExecutionContext): Promise<StandardToolResult> {
  const { name, skeleton, icon, folderId } = params;

  if (!icon || typeof icon !== 'string' || icon.trim().length === 0) {
    return createErrorResult(
      'icon is required for app_project',
      'Provide a Phosphor icon name that matches the app\'s purpose (e.g. "Calculator", "ChartBar", "Heart", "ShoppingCart").',
      'validation'
    );
  }

  const { useCommandRegistryStore } = await import('../../stores/commandRegistryStore');
  const { executeCommand } = useCommandRegistryStore.getState();
  
  return executeCommand('app_builder_create', {
    name,
    template: skeleton?.template ?? 'react-spa',
    description: skeleton?.description ?? '',
    icon,
    ...(folderId ? { folderId } : {}),
  }, context) as Promise<StandardToolResult>;
}

// ============================================================================
// ENTITY CREATION HANDLER (v2 entities: Notes, Sheets, Canvas, Presentations)
// ============================================================================
async function handleEntityCreate(params: any, context: CommandExecutionContext): Promise<StandardToolResult> {
    const startTime = Date.now();
    let { entityType, name, folderId } = params;
    
    // Normalize to canonical types (lowercase to capitalized)
    const typeMap: Record<string, string> = {
      'sheet': 'Sheet',
      'note': 'Note',
      'canvas': 'Canvas',
      'presentation': 'Presentation',
    };
    
    const normalized = typeMap[entityType.toLowerCase()];
    if (normalized) {
      entityType = normalized;
    } else {
      return createErrorResult(
        `Unsupported entity type: "${entityType}"`,
        `Allowed types: doc | note | sheet | canvas | presentation | app_project`,
        'validation',
        { data: { entityType } }
      );
    }
    
    // ✅ Always use current space from context
    const spaceId = context.spaceId;
    
    try {
      if (!name || name.trim().length === 0) {
        return createErrorResult(
          'Entity name cannot be empty',
          'Name is required',
          'validation',
          { data: { spaceId, entityType } }
        );
      }
      
      const autoSkeleton = {
        '@type': entityType,
        version: 'v2',
        architecture: 'unified',
        groupIds: folderId ? [folderId] : [],
        ...params.skeleton
      };
      
      const entityStore = useEntityStore.getState();
      const entity = await entityStore.actions.createEntity({
        entityType,
        name: name.trim(),
        spaceId,
        skeleton: autoSkeleton
      });
      
      // Open window
      let windowInfo = null;
      
      const windowEligibleTypes = ['Sheet', 'Presentation', 'Note', 'Canvas', 'StoryCharacter'];
      if (windowEligibleTypes.includes(entityType)) {
        try {
          const { useCommandRegistryStore } = await import('../../stores/commandRegistryStore');
          const { executeCommand } = useCommandRegistryStore.getState();
          
          if (!useCommandRegistryStore.getState().getCommandDefinition('space_open_window')) {
            throw new Error('space_open_window command not found in registry');
          }
          
          const entityId = entity._id || entity.id;
          const entityLlmId = entity.llmId;
          
          const result = (await executeCommand('space_open_window', {
            entityId
          })) as StandardToolResult;
          
          if (result && result.status === 'success') {
            windowInfo = {
              id: result.data?.windowId,
              title: name.trim(),
              appType: result.data?.appType,
              entityId,
              entityLlmId
            };
          }
        } catch {
          // Window failed to open — entity was still created successfully
        }
      }
      
      return createSuccessResult(
        `Created ${entityType} "${name.trim()}" successfully${windowInfo ? ' and opened window' : ''}`,
        {
          entityId: entity._id || entity.id,
          llmId: entity.llmId,
          entity: {
            _id: entity._id || entity.id,
            id: entity._id || entity.id,
            llmId: entity.llmId,
            name: entity.name,
            entityType: entity.entityType,
            type: entity.type,
            created_at: entity.created_at,
            updated_at: entity.updated_at,
            skeleton: entity.skeleton
          },
          window: windowInfo
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityStore.actions',
            affectedEntities: [entity._id, ...(windowInfo ? [windowInfo.id] : [])]
          } as any,
          ui: {
            refreshContext: true,
            autoFocus: windowInfo?.id,
            resultPreview: 'summary'
          }
        }
      );
      
    } catch (error: any) {
      return createErrorResult(
        'Entity creation failed',
        `Failed to create entity: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { spaceId, entityType, name },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
}
